<template lang="pug">
div(:class="loadingClass")
  form(@submit.prevent="submit", style="max-width: 640px;", v-cloak)
    .row.row--flex
      .col.l8.s12
        .form__item
          template(v-if="isEdit")
            label.form-ctl__label(for="form-card_name") Name on Card
            .form-ctl--static(v-text="formData.name")
          template(v-else)
            label.form-ctl__label(for="form-card_name").
              Name on Card #[span(class="form-ctl__option") Required]
            input#form-card_name.form-ctl(
            type="text",
            name="cardname",
            v-model="formData.name",
            v-validate="'required'",
            data-vv-as="name on card",
            :class="{error: errors.has('cardname')}"
            )
          .form-ctl__error(v-show="errors.has('cardname')", v-text="errors.first('cardname')")

    .row.row--flex(v-if="isEdit")
      .col.l5.s12
        .form__item
          label.form-ctl__label Card Number
          .form-ctl--static
            img(
            :src="payment && $store.getters['payments/getPaymentMethodImagePath'](payment).src | cloudImageUrl",
            :alt="payment && $store.getters['payments/getPaymentMethodImagePath'](payment).alt"
            height="30"
            )
            span.u-mls Ending in #[strong(v-text="card && card.last4")]

      .col.l4.s7
        .form__item
          label.form-ctl__label(@click="$refs.cardExpiry.focus()").
            Expiration date #[span(class="form-ctl__option") Required]
          .form--inline
            select.form-ctl(name="exp_month", v-model="formData.exp_month", v-validate="'required'", :class="{error: errors.has('cardExpiry')}")
              option(v-for="m in selectableMonths", :value="m") {{m}}
            select.form-ctl(name="exp_year", v-model="formData.exp_year", v-validate="'required'", :class="{error: errors.has('cardExpiry')}")
              option(v-for="y in selectableYears", :value="y") {{y}}

    .row.row--flex(v-else)
      .col.l8.s12
        .form__item
          vue-stripe-element(
            ref="paymentElement",
            type="payment",
            :stripe-key="stripeKey",
            :options="STRIPE_PAYMENT_ELEMENT_OPTIONS"
            @change="onChangeStripeElement($event)"
          )

    .row.row--flex
      .col.s12
        .form__item
          label.form-ctl__label.
            Billing address
          .form-ctl__static(style="white-space: pre; line-height: 1.2;")
            | {{ cardBillingAddress | formatAddress }}
          a.c-btn(v-if="!cardBillingAddress", href="javascript:void(0)", @click="addressEditState = ADDRESS_EDIT_STATES.ADD") Input a billing address
          a(v-else-if="inputtedAddress", href="javascript:void(0)", @click="addressEditState = ADDRESS_EDIT_STATES.ADD") Change
          a(v-else, href="javascript:void(0)", @click="addressEditState = ADDRESS_EDIT_STATES.SELECT") Change

    .row.row--flex
      .col.s6
        button.c-btn.c-btn--primary.c-btn--fill(type="submit") Submit
      .col.s12
        div(class="c-message c-message--warn sg") Please ensure your billing address matches your payment information. An inaccurate billing address may result in order complications.


  modal(
  name="billing-address",
  :max-width="720",
  width="100%",
  height="auto",
  :min-height="90",
  :scrollable="true",
  :adaptive="true",
  :click-to-close="false",
  @opened="onOpenedAddressForm"
  )
    .c-panel
      .c-panel__header.u-flex.u-flex-jc-space_between.u-flex-ai-center
        strong(v-if="addressEditState === ADDRESS_EDIT_STATES.SELECT") Select a billing address
        strong(v-if="addressEditState === ADDRESS_EDIT_STATES.ADD") Add a new billing address
        button.c-btn.c-btn--link-inherit(@click="addressEditState = ADDRESS_EDIT_STATES.DEFAULT")
          i.icon-remove
      .c-panel__body
        .row.row--flex(v-if="addressEditState === ADDRESS_EDIT_STATES.SELECT")
          .col.s12.l6.u-mts(v-for="address in addresses")
            .c-panel.z-depth-1(style="height: 100%")
              .c-panel__body.u-flex.u-flex-dir-column(style="height: 100%;")
                div
                  span.u-block
                    strong {{address.name}}
                  span.u-block(style="white-space: pre;") {{ address | formatAddress }}

                button.c-btn.c-btn--primary.c-btn--fill.u-mts(
                v-if="!isEdit || (payment.stripe && payment.stripe.shippings && payment.stripe.shippings[0] !== address._id)",
                @click="selectBillingAddress(address._id)"
                ) Use this address
          .col.l4.s12.u-mts
            a.c-panel.c-panel--translucent.z-depth-1(href="javascript:void(0)", @click="addressEditState = ADDRESS_EDIT_STATES.ADD", style="display: block; height: 100%")
              .c-panel__body.v-add_card
                .c-btn.c-btn--primary.c-btn--circle
                  i.icon-plus
                div(style="font-size: 14px; color: #1C74D1; margin-top: 10px;") Add a new address

        .row.row--flex(v-if="addressEditState === ADDRESS_EDIT_STATES.ADD")
          .col.s12
            vue-shipping-input(
              ref="addressForm",
              :service="service",
              :skip-add="addressAddSkip",
              :skip-change-current="true",
              @submit="inputBillingAddress"
            )
            button.c-btn.c-btn--link-inherit(@click="addressEditState = ADDRESS_EDIT_STATES.SELECT") Back

  //- recaptcha
  vue-recaptcha(
    ref="recaptcha"
    size="invisible"
    :sitekey="recaptchaSiteKey"
    @verify="onCaptchaVerified"
    @expired="onCaptchaExpired"
    @error="onCaptchaError"
  )
</template>

<script>
import { Stripe } from '../stripeElements/stripeElements';
import { PAYMENT_METHOD } from '../../constants/shop';

import urlUtil from '../../util/url';
import addressUtil from '../../util/address';
import numberUtil from '../../util/number';

import stripeElementComponent from '../stripeElements/StripeElement.vue';
import shippingInputComponent from '../shippings/ShippingsInput.vue';
import { VueRecaptcha } from 'vue-recaptcha';
import paymentsApi from '../../api/payments';

const ADDRESS_EDIT_STATES = Object.freeze({
  DEFAULT: 'default',
  SELECT: 'select',
  ADD: 'add'
});

const STRIPE_PAYMENT_ELEMENT_OPTIONS = {
  initialization: {
    mode: 'setup',
    currency: 'usd',
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['card']
  },
  element: {
    terms: {
      card: 'never'
    },
    fields: {
      billingDetails: {
        address: {
          country: 'never'
        }
      }
    },
    wallets: {
      applePay: 'never',
      googlePay: 'never',
      link: 'never'
    }
  }
};

export default {
  name: 'PaymentManageInput',

  filters: {
    cloudImageUrl: urlUtil.cloudImageUrl,
    formatAddress: addressUtil.format
  },

  components: {
    'vue-stripe-element': stripeElementComponent,
    'vue-shipping-input': shippingInputComponent,
    'vue-recaptcha': VueRecaptcha
  },

  props: {
    service: {
      type: String,
      default: 'shop'
    },

    paymentId: String,
    recaptchaSiteKey: String,
    stripeKey: String,

    addressAddSkip: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: true,
      addressEditState: ADDRESS_EDIT_STATES.DEFAULT,
      selectedAddressId: null,
      inputtedAddress: null,
      completes: {
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false
      },
      formData: {
        name: '',
        exp_month: 0,
        exp_year: 0
      }
    };
  },

  created() {
    this.fetchAddresses();
    if (this.isEdit) {
      this.fetch().then(() => {
        this.formData.name = this.card.name;
        this.formData.exp_month = this.card.exp_month;
        this.formData.exp_year = this.card.exp_year;
      });
    }
  },

  computed: {
    loadingClass() {
      return {
        'c-loading': this.loading,
        'c-loading--opacity': this.loading,
        'v-loading': this.loading
      };
    },

    isEdit() {
      return this.paymentId !== 'new';
    },

    payment() {
      if (!this.isEdit) {
        return {};
      }
      return this.$store.getters['payments/findById'](this.paymentId);
    },

    card() {
      if (!this.isEdit || !this.payment) {
        return {};
      }
      return this.$store.getters['payments/findCardById'](
        this.payment.stripe.card_id
      );
    },

    cardBillingAddress() {
      // shippings から選択されているアドレスがあれば最優先でその住所を返す
      if (this.selectedAddressId) {
        return this.$store.getters['shippings/findById'](
          this.selectedAddressId
        );
      }
      // 入力された住所があればその住所を返す
      else if (this.inputtedAddress) {
        return this.inputtedAddress;
      }
      // 住所に変更がなく、カード情報編集時は登録済み住所を返す
      else if (this.isEdit) {
        return {
          line1: this.card.address_line1,
          line2: this.card.address_line2,
          city: this.card.address_city,
          state: this.card.address_state,
          zip: this.card.address_zip,
          country: this.card.address_country
        };
      }
      // 新規カード登録の場合
      else {
        // デフォルト住所があればそれを返す
        if (this.$store.getters['shippings/current']) {
          return this.$store.getters['shippings/current'];
        }
        // アドレスが1件も登録されていない場合はnullを返す
        return null;
      }
    },

    addresses() {
      return this.$store.getters['shippings/list'];
    },

    selectableMonths() {
      return numberUtil.range(1, 12);
    },

    selectableYears() {
      const year = new Date().getFullYear();
      return numberUtil.range(year, year + 20);
    },

    ADDRESS_EDIT_STATES() {
      return ADDRESS_EDIT_STATES;
    },

    STRIPE_PAYMENT_ELEMENT_OPTIONS() {
      return STRIPE_PAYMENT_ELEMENT_OPTIONS;
    }
  },

  methods: {
    fetch() {
      this.loading = true;
      return this.$store
        .dispatch('payments/fetchAll')
        .then(() => {
          this.loading = false;
        })
        .catch((err) => {
          if (err && err.response && err.response.status === 401) {
            this.$emit('auth-error');
          } else {
            this.loading = false;

            const errorMessage = err.general || String(err);
            this.$alert(errorMessage);
          }
        });
    },

    fetchAddresses() {
      this.loading = true;
      this.$store
        .dispatch('shippings/fetchAll')
        .then(() => {
          this.loading = false;
          if (!this.isEdit && !this.cardBillingAddress) {
            if (this.addresses.length === 0) {
              this.addressEditState = ADDRESS_EDIT_STATES.ADD;
            } else {
              // 新規登録でデフォルトアドレスもない場合1つ目のアドレスを選択アドレスにする
              this.selectedAddressId = this.addresses[0]._id;
            }
          }
          if (
            this.addresses.length === 0 &&
            !this.isEdit &&
            !this.cardBillingAddress
          ) {
            this.addressEditState = ADDRESS_EDIT_STATES.ADD;
          }
        })
        .catch((err) => {
          const errorMessage = err.general || String(err);
          this.$alert(errorMessage);
        })
        .then(() => {
          this.loading = false;
        });
    },

    onChangeStripeElement(event) {
      this.completes[event.elementType] = event.complete;

      // Add to ErrorBag in vee-validate
      this.errors.remove(event.elementType);
      if (event.error) {
        this.errors.add({
          field: event.elementType,
          msg: event.error.message
        });
      }
    },

    stripeUpdate() {
      Object.keys(this.completes).some((key) => {
        if (!this.completes[key]) {
          this.$refs[key].focus();
          return true;
        }
      });
    },

    selectBillingAddress(selectedId) {
      this.selectedAddressId = selectedId;
      this.addressEditState = ADDRESS_EDIT_STATES.DEFAULT;
    },

    inputBillingAddress() {
      const address = this.$refs.addressForm.formData;
      this.inputtedAddress = {
        label: address.label,
        name: address.name,
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country,
        tel: address.tel
      };
      this.addressEditState = ADDRESS_EDIT_STATES.DEFAULT;
    },

    submit() {
      // validation
      if (!this.cardBillingAddress) {
        this.addressEditState = ADDRESS_EDIT_STATES.ADD;
        return;
      }

      this.$validator.reset();
      this.$validator.validate().then((result) => {
        if (result) {
          if (this.isEdit) {
            this.$_editCard();
          } else {
            this.$_addCard();
          }
        }
      });
    },

    $_addCard() {
      this.$refs.recaptcha.execute();
    },

    $_editCard() {
      // vee-validate ErrorBag
      this.errors.remove('cardExpiry');
      if (!this.formData.exp_month || !this.formData.exp_year) {
        this.errors.add({
          field: 'cardExpiry',
          msg: "Your card's expiration date is incomplete."
        });
        return;
      }
      // 次月の月初と現在の日を比較
      else if (
        new Date(this.formData.exp_year, this.formData.exp_month, 1) <
        new Date()
      ) {
        this.errors.add({
          field: 'cardExpiry',
          msg: "Your card's expiration year is in the past."
        });
        return;
      }

      const address = this.cardBillingAddress;
      const params = {
        payment_id: this.payment._id,
        address_line1: address.line1,
        address_line2: address.line2,
        address_city: address.city,
        address_state: address.state,
        address_zip: address.zip,
        address_country: address.country,
        exp_month: this.formData.exp_month,
        exp_year: this.formData.exp_year
      };

      this.$store
        .dispatch('payments/updateCard', params)
        .then((res) => {
          this.loading = false;
          this.$emit('updated', res);
        })
        .catch((err) => {
          this.loading = false;

          const errorMessage = err.general || String(err);
          this.$alert(errorMessage);
        });
    },

    onOpenedAddressForm() {
      if (this.addressEditState === ADDRESS_EDIT_STATES.ADD) {
        const formComp = this.$refs.addressForm;
        formComp.inputConfig = {
          LABEL: {
            ignore: true
          }
        };
        if (this.inputtedAddress) {
          formComp.formData.name = this.inputtedAddress.name;
          formComp.formData.country = this.inputtedAddress.country;
          formComp.formData.line1 = this.inputtedAddress.line1;
          formComp.formData.line2 = this.inputtedAddress.line2;
          formComp.formData.city = this.inputtedAddress.city;
          formComp.formData.zip = this.inputtedAddress.zip;
          formComp.formData.tel = this.inputtedAddress.tel;

          // country で state は選択式になる場合があるためさらに再描画後にセットする
          this.$nextTick(function () {
            formComp.formData.state = this.inputtedAddress.state;
            formComp.$el.querySelector('input[name=name]').focus();
          });
        }
      }
    },

    onCaptchaVerified(recaptchaToken) {
      this.$refs.recaptcha.reset();

      this.loading = true;
      return Stripe.elements
        .submit()
        .then((result) => {
          if (result.error) {
            const err = new Error(result.error.message || 'Stripe error.');
            err.stripeError = true;
            throw err;
          }
        })
        .then(
          () =>
            new Promise((resolve, reject) => {
              paymentsApi.createSetupIntent(
                {
                  'g-recaptcha-response': recaptchaToken
                },
                (err, res) => {
                  if (err) {
                    return reject(err);
                  }
                  resolve(res.client_secret);
                }
              );
            })
        )
        .then((clientSecret) => {
          const address = this.cardBillingAddress;
          return Stripe.instance.confirmSetup({
            elements: Stripe.elements,
            clientSecret,
            confirmParams: {
              payment_method_data: {
                billing_details: {
                  name: this.formData.name,
                  address: {
                    line1: address.line1,
                    line2: address.line2,
                    city: address.city,
                    state: address.state,
                    postal_code: address.zip,
                    country: address.country
                  }
                }
              },
              return_url: `${window.location.origin}/settings/payment_methods`
            },
            redirect: 'if_required'
          });
        })
        .then((result) => {
          if (result.error) {
            const err = new Error(result.error.message || 'Stripe error.');
            err.stripeError = true;
            throw err;
          }
          return result.setupIntent.payment_method;
        })
        .then((paymentMethodId) => {
          const params = {
            method: PAYMENT_METHOD.STRIPE,
            shipping_id: this.selectedAddressId,
            recaptcha_token: recaptchaToken,
            payment_method_id: paymentMethodId
          };
          return this.$store.dispatch('payments/add', params);
        })
        .then((res) => {
          this.loading = false;
          this.$emit('added', res.payments[0].payment);
        })
        .catch((err) => {
          this.loading = false;

          if (err.stripeError) {
            this.$alert(err.message);
          } else {
            const errorMessage = err.general || String(err);
            this.$alert(errorMessage);
          }
        });
    },
    onCaptchaExpired() {
      this.loading = false;
      this.$refs.recaptcha.reset();
    },
    onCaptchaError() {
      this.loading = false;
      this.$alert(
        'An error occurred. Please wait a moment before trying again.'
      );
    }
  },

  watch: {
    completes: {
      handler() {
        this.stripeUpdate();
      },
      deep: true
    },
    addressEditState: function (val) {
      if (val === ADDRESS_EDIT_STATES.DEFAULT) {
        this.$modal.hide('billing-address');
      } else {
        this.$modal.show('billing-address');
        if (val === ADDRESS_EDIT_STATES.ADD) {
          this.selectedAddressId = null;
        }
      }
    }
  }
};
</script>

<style scoped>
.v-loading {
  min-height: 50vh;
}

.v-add_card {
  text-align: center;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.form__item {
  margin-bottom: 10px;
}

.form-ctl--static {
  padding: 0;
}

.form-ctl__option {
  padding-left: 0;
}

.grecaptcha-badge {
  z-index: 1;
}
</style>
